import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL;

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f7f5',
    padding: 20,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    padding: '32px 28px',
    textAlign: 'center',
  },
  dateTimeBox: {
    background: '#f3f7f5',
    borderRadius: 14,
    padding: '16px 12px',
    margin: '20px 0',
  },
  dateText: { fontSize: 18, fontWeight: 700, color: '#0b3d2e' },
  timeText: { fontSize: 26, fontWeight: 800, color: '#0b3d2e', marginTop: 4 },
  label: { fontSize: 14, color: '#667', textAlign: 'left', display: 'block', marginBottom: 6, fontWeight: 600 },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 20,
    letterSpacing: 2,
    textAlign: 'center',
    borderRadius: 12,
    border: '2px solid #dfe7e3',
    outline: 'none',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    marginTop: 16,
    padding: '14px 16px',
    fontSize: 16,
    fontWeight: 700,
    color: '#fff',
    background: '#0f9d58',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
  },
  buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  errorBox: { color: '#b3261e', background: '#fdecea', borderRadius: 12, padding: '12px 14px', marginTop: 16, fontSize: 14 },
  successIcon: { fontSize: 54, marginBottom: 8 },
  title: { fontSize: 20, fontWeight: 800, color: '#0b3d2e', marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#667' },
};

function MarkAttendance() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [dateDisplay, setDateDisplay] = useState('');
  const [timeDisplay, setTimeDisplay] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const [idNumber, setIdNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'already'|'error', message, studentName }

  const checkToken = useCallback(async () => {
    setChecking(true);
    try {
      const response = await axios.get(`${apiUrl}/api/public/attendance-mark-info`, { params: { token } });
      if (response.data.success && response.data.valid) {
        setValid(true);
        setDateDisplay(response.data.dateDisplay);
        setTimeDisplay(response.data.timeDisplay);
      } else {
        setValid(false);
        setInfoMessage(response.data.message || 'This QR code is not valid.');
      }
    } catch (err) {
      setValid(false);
      setInfoMessage('Could not reach the server. Please check your connection and try again.');
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      setValid(false);
      setInfoMessage('No QR code detected. Please scan the attendance QR code shown by your instructor.');
      return;
    }
    checkToken();
  }, [token, checkToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idNumber.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const response = await axios.post(`${apiUrl}/api/public/attendance-mark`, { token, idNumber: idNumber.trim() });
      if (response.data.success) {
        setResult({ type: 'success', message: response.data.message, studentName: response.data.studentName, dateDisplay: response.data.dateDisplay, timeDisplay: response.data.timeDisplay });
      } else if (response.data.alreadyMarked) {
        setResult({ type: 'already', message: response.data.message, studentName: response.data.studentName });
      } else {
        setResult({ type: 'error', message: response.data.message || 'Something went wrong. Please try again.' });
      }
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.message || 'Could not reach the server. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={styles.subtitle}>Checking QR code...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successIcon}>⚠️</div>
          <div style={styles.title}>QR Code Not Valid</div>
          <p style={{ ...styles.subtitle, marginTop: 8 }}>{infoMessage}</p>
        </div>
      </div>
    );
  }

  if (result && (result.type === 'success' || result.type === 'already')) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successIcon}>{result.type === 'success' ? '✅' : 'ℹ️'}</div>
          <div style={styles.title}>{result.type === 'success' ? 'Attendance Marked!' : 'Already Marked'}</div>
          {result.studentName && <p style={{ ...styles.subtitle, marginTop: 4, fontWeight: 600 }}>{result.studentName}</p>}
          <p style={{ ...styles.subtitle, marginTop: 8 }}>{result.message}</p>
          {result.type === 'success' && (
            <div style={styles.dateTimeBox}>
              <div style={styles.dateText}>{result.dateDisplay}</div>
              <div style={styles.timeText}>{result.timeDisplay}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.title}>Mark Your Attendance</div>
        <p style={styles.subtitle}>This date and time are recorded automatically and cannot be changed.</p>
        <div style={styles.dateTimeBox}>
          <div style={styles.dateText}>{dateDisplay}</div>
          <div style={styles.timeText}>{timeDisplay}</div>
        </div>
        <form onSubmit={handleSubmit}>
          <label style={styles.label} htmlFor="idNumber">Your Student ID Number</label>
          <input
            id="idNumber"
            type="text"
            inputMode="numeric"
            placeholder="e.g. 0010"
            style={styles.input}
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            disabled={submitting}
            autoFocus
          />
          <p style={{ ...styles.subtitle, marginTop: 6, fontSize: 12 }}>
            Just the number in the middle of your ID (e.g. for DMFM-0010-2025, type 0010).
          </p>
          <button
            type="submit"
            style={{ ...styles.button, ...(submitting || !idNumber.trim() ? styles.buttonDisabled : {}) }}
            disabled={submitting || !idNumber.trim()}
          >
            {submitting ? 'Marking...' : 'Mark Attendance'}
          </button>
        </form>
        {result && result.type === 'error' && <div style={styles.errorBox}>{result.message}</div>}
      </div>
    </div>
  );
}

export default MarkAttendance;
